import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { sortTasks } from "../../helpers/sortTasks";
import { CollectionDto } from "./collection/collection.types";
import {
  createScheduleItem,
  deleteScheduleItem,
  updateScheduleItem,
} from "../calendar/scheduleItem.thunk";
import {
  ActiveTaskDto,
  CreateTaskThunkArg,
  DelegationStatus,
  GetArchivedNotes,
  GetTaskDto,
  TaskDto,
  noteStateType,
} from "./types";
import {
  deleteTask,
  createQuickTask,
  getTasks,
  updateTask,
  getTask,
  createAssetData,
  updateAssetLinkData,
  startNoteRecord,
  endNoteRecord,
  getNoteTimerRecords,
  deleteNoteRecord,
  createTaskAccess,
  replaceTaskAccess,
  deleteTaskAccess,
  finishTask,
  getCompletedTasks,
  getActiveTask,
  createTask,
  getDeletedTasks,
  getArchivedTask,
  restoreArchivedTask,
  updateNoteRecord,
  togglePauseTimer,
  getActiveRecording,
  getRecordingsInRange,
  getReflectWeek,
  createTasks,
} from "./taskThunk";
import {
  acceptRequest,
  createDelegationRequest,
  createRequestAccess,
  deleteRequest,
  deleteRequestAccess,
  getRequestedTask,
  getRequests,
  rejectRequest,
} from "./delegation/delegation.thunk";
import { getMe } from "../users/users.thunk";
import { addCollectionReducers } from "./collection/collection.extraReducers";
import { addProjectReducers } from "./project/project.extraReducers";
import { addObjectiveLinkReducers } from "./objectiveLink.extraReducers";
import { getProjectCriticality } from "../../helpers/projectStatus";
import {
  calculateUrgencyForDueTasks,
  getInheritedDeadlines,
} from "../../helpers/effectiveDueDate";

// Urgency comes from the effective deadline (own, list or project end)
export { calculateUrgencyForDueTasks };

const initialState: noteStateType = {
  tasks: [],
  isError: false,
  isSuccess: false,
  isLoading: false,
  taskIsCompleted: false,
  message: "",
  collection: undefined,
  meta: {
    taskDataInitialized: false,
    completedTasksLoaded: false,
    completedTasksLoadedCount: 0,
  },
  collections: [],
  recorder: {
    recordingActive: false,
  },
};

export const taskSlice = createSlice({
  name: "tasks",
  initialState,
  reducers: {
    resetTaskStatus: (state) => {
      state.isLoading = false;
      state.isError = false;
      state.isSuccess = false;
    },
    resetTaskIsCompleted: (state) => {
      state.taskIsCompleted = false;
    },
    resetArchivedTask: (state) => {
      state.archivedTask = undefined;
    },
    setTaskIsDeleted: (state, action: PayloadAction<boolean>) => {
      state.taskIsDeleted = action.payload;
    },
    setTaskIsCompleted: (state) => {
      state.taskIsCompleted = true;
    },
    resetTaskIsNew: (state) => {
      state.tasks = state.tasks.map((task) => {
        return { ...task, isNew: false };
      });
      state.task = state.task ? { ...state.task, isNew: false } : undefined;
    },
    resetTask: (state) => {
      state.task = undefined;
    },
    setActiveTaskData: (
      state,
      action: PayloadAction<{
        taskData: ActiveTaskDto | null;
      }>,
    ) => {
      state.activeTask = action.payload.taskData;
    },
    setRecordingStartTime: (state, action) => {
      state.recorder.recorderStartTime = action.payload;
    },
    setPomodoroStartTime: (
      state,
      action: PayloadAction<number | undefined>,
    ) => {
      state.recorder.pomodoroStartTime = action.payload;
    },
    setTotalRecordedWorkload: (state, action) => {
      state.recorder.totalRecordedWorkload = action.payload;
    },
    setEstimatedWorkload: (state, action) => {
      state.recorder.estimatedWorkload = action.payload;
    },
    setScheduledTime: (state, action) => {
      state.recorder.scheduledWorkload = action.payload;
    },
    setRecordingCompletionValue: (state, action) => {
      state.recorder.completionValue = action.payload;
    },
  },
  extraReducers: (builder) => {
    addCollectionReducers(builder);
    addProjectReducers(builder);
    addObjectiveLinkReducers(builder);

    builder
      .addCase(deleteTask.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        const refId = action.payload.finishedTaskId;
        state.deletedTasks?.push(action.payload.archivedTask);

        state.tasks = state.tasks.filter((task) => {
          return task.id !== refId;
        });

        if (state.collection) {
          state.collection = {
            ...state.collection,
            notes: state.collection?.notes?.filter(
              (note) => note.noteId !== refId,
            ),
          };
        }

        state.collections = state.collections?.map((collection) => {
          collection.notes = collection.notes?.filter((note) => {
            return note.noteId !== refId;
          });
          return collection;
        });

        if (refId === state.activeTask?.id) {
          state.recorder.recordingActive = false;
          state.activeTask = undefined;
        }
        state.taskIsDeleted = true;

        if (state.projects) {
          state.projects = state.projects.map((proj) => {
            if (
              // Is in collection
              (proj.Collections &&
                proj.Collections.some((a) =>
                  (
                    action.payload.archivedTask.collections as string[]
                  ).includes(a.id),
                )) ||
              // Is in index Collectio
              (proj.inboxCollection &&
                (action.payload.archivedTask.collections as string[]).includes(
                  proj.inboxCollection.id,
                ))
            ) {
              // mutate and return the matched project
              return {
                ...proj,
                unfinishedNotes:
                  proj.unfinishedNotes - 1 /* your mutation here */,
              };
            }
            return proj;
          });
          action.payload.archivedTask.collections;
        }

        // Project
        if (state.project) {
          const allCollections = [
            state.project.inboxCollection,
            ...(state.project.Collections || []),
          ];
          const foundInProject = allCollections.some((a) =>
            a.notes?.some((a) => a.noteId === refId),
          );
          if (foundInProject) {
            state.project.inboxCollection.notes =
              state.project.inboxCollection.notes?.filter(
                (note) => note.noteId !== refId,
              );
            state.project.Collections = state.project.Collections?.map(
              (collection) => ({
                ...collection,
                notes: collection.notes?.filter(
                  (note) => note.noteId !== refId,
                ),
              }),
            );
            state.project = {
              ...state.project,
              unfinishedNotes: state.project.unfinishedNotes - 1,
              ...getProjectCriticality(state.project),
            };
          }
        }
        // Projects
      })
      .addCase(deleteTask.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(createTasks.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createTasks.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;

        state.tasks = [
          ...state.tasks,
          ...action.payload.notesData,
        ] as GetTaskDto[];

        if (state.collection && action.payload.collectionId) {
          const len = state.collection.notes
            ? state.collection.notes.length
            : 0;
          state.collection.notes = [
            ...(state.collection.notes || []),
            ...action.payload.notesData.map((note, index) => ({
              ...note,
              positionInList: len + index + 1,
              noteId: note.id,
              collectionId: action.payload.collectionId as string,
            })),
          ];
        }
      })
      .addCase(createTasks.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })

      .addCase(createTask.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createTask.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        const suppress = (action.meta.arg as CreateTaskThunkArg).__opts
          ?.suppressNewMarker;
        state.tasks = state.tasks.map((t) => ({ ...t, isNew: false }));

        const payloadWithFlag = {
          ...(action.payload as GetTaskDto),
          // The create answer has no NoteAccess; keep it in the shape of a
          // loaded task
          NoteAccess: (action.payload as GetTaskDto).NoteAccess ?? [],
          isNew: !suppress,
        } as GetTaskDto & { isNew?: boolean };

        state.tasks = [...state.tasks, payloadWithFlag] as GetTaskDto[];
        state.task = payloadWithFlag;
      })
      .addCase(createTask.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })

      .addCase(createQuickTask.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createQuickTask.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.tasks = [
          ...state.tasks,
          { ...action.payload, isNew: true },
        ] as GetTaskDto[];
        state.task = { ...action.payload, isNew: true };
      })
      .addCase(createQuickTask.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(getTasks.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
      })
      .addCase(getTasks.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        // Create a copy of the array with the spread operator and then sort it
        state.tasks = calculateUrgencyForDueTasks(
          sortTasks([...action.payload] as GetTaskDto[]),
          getInheritedDeadlines(state.projects, state.project),
        );
        state.meta.taskDataInitialized = true;
      })
      .addCase(getTasks.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })

      .addCase(getCompletedTasks.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
      })
      .addCase(getCompletedTasks.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        // Create a copy of the array with the spread operator and then sort it

        let newlyLoadedTasks: GetArchivedNotes[] = action.payload.filter(
          (finishedTask) => {
            if (
              !state.completedTasks?.some(
                (loadedTask) => loadedTask.id === finishedTask.id,
              )
            )
              return finishedTask;
          },
        );

        if (action.payload.length === 0 || !newlyLoadedTasks) {
          state.meta.completedTasksLoaded = true;
        }

        state.completedTasks = [
          ...(state.completedTasks || []),
          ...newlyLoadedTasks,
        ];

        state.meta.taskDataInitialized = true;
      })
      .addCase(getCompletedTasks.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(getDeletedTasks.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
      })
      .addCase(getDeletedTasks.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        // Create a copy of the array with the spread operator and then sort it

        state.deletedTasks = action.payload;
      })
      .addCase(getDeletedTasks.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })

      .addCase(getArchivedTask.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
      })
      .addCase(getArchivedTask.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        // Create a copy of the array with the spread operator and then sort it

        state.archivedTask = action.payload;
      })
      .addCase(getArchivedTask.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })

      .addCase(updateTask.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateTask.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        // Urgency is computed after merging: the answer may only hold the
        // changed fields, and the floor from list deadlines needs them all
        const inherited = getInheritedDeadlines(state.projects, state.project);
        const updatedTask = action.payload;
        const withUrgency = <T extends TaskDto>(task: T) =>
          calculateUrgencyForDueTasks([task], inherited)[0];
        // Update the current task if it is the one being updated
        if (state.task?.id === updatedTask.id) {
          state.task = withUrgency({
            ...state.task,
            isNew: false,
            ...updatedTask,
            NoteAccess: state.task.NoteAccess,
          });
        }

        if (state?.activeTask?.id === updatedTask.id) {
          state.activeTask = { ...state.activeTask, ...updatedTask };
        }

        state.tasks = state.tasks.map((ta) => {
          if (ta.id === updatedTask.id) {
            return withUrgency({
              ...ta, // keep old fields like NoteAccess, etc.
              ...updatedTask, // override with updated fields
            });
          }
          return ta;
        });

        // Re-sort the tasks array to ensure order after update/addition
        //state.tasks = sortTasks([...state.tasks] as GetTaskDto[]);
      })
      .addCase(updateTask.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.error.message || "Failed to update task";
      })
      .addCase(getTask.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(getTask.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;

        const task = calculateUrgencyForDueTasks(
          [action.payload],
          getInheritedDeadlines(state.projects, state.project),
        )[0];
        console.log(task);

        state.task = task;
      })
      .addCase(getTask.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })

      // History: own status, doesn't touch the global isLoading flags.
      // Only the latest request's answer is kept (ranges can change quickly).
      .addCase(getRecordingsInRange.pending, (state, action) => {
        const { from, to } = action.meta.arg;
        const sameRange =
          state.recordingsHistory?.from === from &&
          state.recordingsHistory?.to === to;
        state.recordingsHistory = {
          from,
          to,
          // Keep showing the old items while reloading the same range
          items: sameRange ? state.recordingsHistory?.items || [] : [],
          status: "loading",
          requestId: action.meta.requestId,
        };
      })
      .addCase(getRecordingsInRange.fulfilled, (state, action) => {
        if (state.recordingsHistory?.requestId !== action.meta.requestId)
          return;
        state.recordingsHistory.items = action.payload;
        state.recordingsHistory.status = "succeeded";
        state.recordingsHistory.error = undefined;
      })
      .addCase(getRecordingsInRange.rejected, (state, action) => {
        if (state.recordingsHistory?.requestId !== action.meta.requestId)
          return;
        state.recordingsHistory.status = "failed";
        state.recordingsHistory.error =
          (action.payload as string) || action.error.message;
      })
      // Rückblick: cached per week, so switching back and forth is instant.
      // Each week keeps its own request id - a late answer can't overwrite
      // a newer one for the same week.
      .addCase(getReflectWeek.pending, (state, action) => {
        const key = action.meta.arg.from;
        state.reflectWeeks = state.reflectWeeks || {};
        state.reflectWeeks[key] = {
          // Keep showing the cached week while it reloads
          data: state.reflectWeeks[key]?.data,
          status: "loading",
          requestId: action.meta.requestId,
        };
      })
      .addCase(getReflectWeek.fulfilled, (state, action) => {
        const entry = state.reflectWeeks?.[action.meta.arg.from];
        if (!entry || entry.requestId !== action.meta.requestId) return;
        entry.data = action.payload;
        entry.status = "succeeded";
        entry.error = undefined;
      })
      .addCase(getReflectWeek.rejected, (state, action) => {
        const entry = state.reflectWeeks?.[action.meta.arg.from];
        if (!entry || entry.requestId !== action.meta.requestId) return;
        entry.status = "failed";
        entry.error = (action.payload as string) || action.error.message;
      })
      .addCase(getActiveRecording.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(getActiveRecording.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(getActiveRecording.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        if (action.payload) {
          state.recorder.activeRecording = action.payload;

          console.log(action.payload);

          state.recorder.recordingActive = true;

          const startedAtMs = new Date(action.payload.startedAt).getTime();
          state.recorder.recorderStartTime = startedAtMs;
          if (action.payload.noteId) {
            state.activeTask = {
              id: action.payload.noteId,
              title: action.payload.Note?.title || "",
              timerRecords: action.payload.Note?.recordedTimes || [],
            };
          }
        }
      })
      .addCase(getActiveTask.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(getActiveTask.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.activeTask = action.payload;
        const activeTask = action.payload;
        if (!activeTask) return;
        state.recorder.recordingActive = action.payload ? true : false;
        const ongoingRecord = activeTask?.timerRecords?.find((r) => !r.endedAt);

        if (ongoingRecord) {
          // Mark it active in Redux
          const startedAtMs = new Date(ongoingRecord.startedAt).getTime();
          state.recorder.recorderStartTime = startedAtMs;
        }
      })
      .addCase(getActiveTask.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })

      .addCase(createAssetData.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(createAssetData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        if (state.task?.assetLinks) {
          state.task.assetLinks.push(action.payload);
        } else
          state.task = {
            ...(state.task as TaskDto),
            assetLinks: [action.payload],
          };
      })

      .addCase(getMe.fulfilled, (state, action) => {
        if (action.payload.activeRecordingId) {
          state.recorder.activeRecordingId = action.payload.activeRecordingId;
          state.recorder.recordingActive = true;
        }
      })

      .addCase(createAssetData.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(updateAssetLinkData.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(updateAssetLinkData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        if (state.task?.assetLinks) {
          state.task.assetLinks = state.task.assetLinks.map((assetLink) =>
            assetLink.assetType === action.payload.assetType
              ? action.payload
              : assetLink,
          );
        }
      })
      .addCase(updateAssetLinkData.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })

      .addCase(togglePauseTimer.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(togglePauseTimer.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(togglePauseTimer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.recorder.activeRecording = action.payload;
        if (action.payload.endedAt) {
          // Ended a pause
          state.recorder.recordingActive = false;
        } else {
          // Started a pause
          state.recorder.recordingActive = true;
        }
      })

      .addCase(startNoteRecord.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(startNoteRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.recorder.taskId = action.payload.noteId;
        state.recorder.recordingActive = true;
        state.recorder.activeRecording = action.payload;

        if (state.task?.timerRecords) {
          state.task.timerRecords.push(action.payload);
        } else if (state.task) {
          state.task = { ...state.task, timerRecords: [action.payload] };
        }

        state.tasks = [...state.tasks].map((a) =>
          a.id === action.payload.noteId
            ? { ...a, kanbanStatus: "PROGRESS" }
            : a,
        );

        state.activeTask = {
          id: action.payload.noteId!,
          title: action.payload?.Note?.title || "",
          timerRecords: state.task?.timerRecords || [],
        };
      })
      .addCase(startNoteRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(updateNoteRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;

        if (state.task?.timerRecords) {
          state.task.timerRecords = state.task.timerRecords.map((rec) => {
            if (rec.id === action.payload.id) {
              return action.payload;
            } else {
              return rec;
            }
          });
        }

        // Replaced, not added - otherwise the edited time counts twice
        if (state.activeTask?.timerRecords) {
          state.activeTask.timerRecords = state.activeTask.timerRecords.map(
            (rec) => (rec.id === action.payload.id ? action.payload : rec),
          );
        }
      })
      .addCase(updateNoteRecord.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })

      .addCase(updateNoteRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })

      .addCase(endNoteRecord.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(endNoteRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.recorder.recorderStartTime = undefined;
        state.recorder.taskId = undefined;

        if (state.task?.timerRecords) {
          state.task.timerRecords = state.task.timerRecords.map((record) => {
            return record.id === action.payload.id ? action.payload : record;
          });
        } else if (state.task) {
          state.task = { ...state.task, timerRecords: [action.payload] };
        }

        state.tasks = [...state.tasks].map((a) =>
          a.id === action.payload.noteId
            ? { ...a, kanbanStatus: "WAITING" }
            : a,
        );

        state.recorder.recordingActive = false;
        state.recorder.activeRecording = undefined;
        state.activeTask = undefined;
        state.recorder.recorderStartTime = undefined;
      })
      .addCase(endNoteRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(getNoteTimerRecords.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(getNoteTimerRecords.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        if (state.task) {
          state.task = { ...state.task, timerRecords: action.payload };
        }
      })
      .addCase(getNoteTimerRecords.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(deleteNoteRecord.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(deleteNoteRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        if (state.task) {
          state.task.timerRecords = state.task?.timerRecords?.filter(
            (record) => record.id != action.payload,
          );
        }

        if (state.activeTask) {
          state.activeTask.timerRecords = state.task?.timerRecords?.filter(
            (record) => record.id != action.payload,
          );
        }
      })
      .addCase(deleteNoteRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })

      .addCase(createScheduleItem.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(createScheduleItem.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        if (state.task?.schedule) {
          state.task.schedule.push(action.payload);
        } else if (state.task) {
          state.task.schedule = [action.payload];
        }
      })
      .addCase(createScheduleItem.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })

      .addCase(restoreArchivedTask.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(restoreArchivedTask.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(restoreArchivedTask.fulfilled, (state, action) => {
        // The restored task is a new note with a new id. Its slots are moved
        // over in the calendar slice; here the old id stops counting as
        // finished, otherwise today's progress would still count it as done.
        const { originalNoteId, NoteScheduling, ...task } =
          action.payload.task;
        // Not isNew: TaskItem would open the title for editing and focus it,
        // which blocks swiping the restored task
        state.tasks = [
          ...state.tasks,
          { ...task, restoredFrom: originalNoteId },
        ];
        state.finishedTaskIds = state.finishedTaskIds?.filter(
          (id) => id !== originalNoteId,
        );
        state.deletedTasks = state.deletedTasks?.filter(
          (ta) => ta.id !== action.payload.archivedTaskId,
        );
        state.completedTasks = state.completedTasks?.filter(
          (ta) => ta.id !== action.payload.archivedTaskId,
        );
        // The backend puts it back into its old columns (the project is
        // reloaded for that), the "done" column just has to let go of it
        if (state.project) {
          const notFinished = <C extends CollectionDto>(collection: C): C => ({
            ...collection,
            finishedNotes: collection.finishedNotes?.filter(
              (note) => note.id !== action.payload.archivedTaskId,
            ),
          });
          state.project.inboxCollection = notFinished(
            state.project.inboxCollection,
          );
          state.project.Collections =
            state.project.Collections?.map(notFinished);
        }
        state.isLoading = false;
        state.isSuccess = true;
      })
      .addCase(finishTask.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(finishTask.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(finishTask.fulfilled, (state, action) => {
        state.taskIsCompleted = true;
        const refId = action.payload.finishedTaskId;

        //Collection
        if (state.collection) {
          state.collection = {
            ...state.collection,
            notes: state.collection.notes?.filter(
              (note) => note.noteId !== refId,
            ),
          };
        }

        //Collections
        if (state.collections) {
          state.collections = state.collections.map((collection) => {
            return {
              ...collection,
              notes: collection.notes?.filter((note) => note.noteId !== refId),
            };
          });
        }

        if (state.projects) {
          state.projects = state.projects.map((proj) => {
            if (
              // Is in collection
              (proj.Collections &&
                proj.Collections.some((a) =>
                  (
                    action.payload.archivedTask.collections as string[]
                  ).includes(a.id),
                )) ||
              // Is in index Collection
              (proj.inboxCollection &&
                (action.payload.archivedTask.collections as string[]).includes(
                  proj.inboxCollection.id,
                ))
            ) {
              // mutate and return the matched project
              return {
                ...proj,
                unfinishedNotes: proj.unfinishedNotes - 1,
                finishedNotes: proj.finishedNotes + 1,
              };
            }
            return proj;
          });
          action.payload.archivedTask.collections;
        }

        //Project
        if (state.project) {
          const allCollections = [
            state.project.inboxCollection,
            ...(state.project.Collections || []),
          ];
          const foundInProject = allCollections.some((a) =>
            a.notes?.some((a) => a.noteId === refId),
          );
          if (foundInProject) {
            const { archivedTask } = action.payload;
            // Moves the task from the column into the "done" column
            const toFinished = <C extends CollectionDto>(collection: C): C => ({
              ...collection,
              notes: collection.notes?.filter((note) => note.noteId !== refId),
              finishedNotes: archivedTask.collections.includes(collection.id)
                ? [
                    { ...archivedTask, collectionId: collection.id },
                    ...(collection.finishedNotes || []),
                  ]
                : collection.finishedNotes,
            });

            state.project.inboxCollection = toFinished(
              state.project.inboxCollection,
            );
            state.project.Collections =
              state.project.Collections?.map(toFinished);

            state.project = {
              ...state.project,
              unfinishedNotes: state.project.unfinishedNotes - 1,
              finishedNotes: state.project.finishedNotes + 1,
              ...getProjectCriticality(state.project),
            };
          }
        }

        state.tasks = state.tasks.filter((task) => {
          return task.id !== refId;
        });

        state.completedTasks?.push(action.payload.archivedTask);
        state.finishedTaskIds = [...(state.finishedTaskIds || []), refId];

        if (refId === state.activeTask?.id) {
          state.recorder.recordingActive = false;
          state.activeTask = undefined;
        }
        state.isLoading = false;
        state.isSuccess = true;
      })
      .addCase(createTaskAccess.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;

        const noteId = action.payload.noteId;

        if (state.tasks) {
          state.tasks = state.tasks.map((task) => {
            if (task.id === noteId) {
              return {
                ...task,
                NoteAccess: [...(task.NoteAccess || []), action.payload],
              };
            } else return task;
          });
        }

        if (state.task?.id === noteId) {
          state.task.NoteAccess = [
            ...(state.task?.NoteAccess || []),
            action.payload,
          ];
        }
      })
      .addCase(createTaskAccess.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(createTaskAccess.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(replaceTaskAccess.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;

        const noteId = action.payload.accessOption.noteId;

        if (state.tasks) {
          state.tasks = state.tasks.map((task) => {
            if (task.id === noteId) {
              return {
                ...task,
                NoteAccess: task.NoteAccess.map((access) => {
                  if (access.id == action.payload.deletedAccess)
                    return action.payload.accessOption;
                  else return access;
                }),
              };
            } else return task;
          });
        }

        if (state.task) {
          if (state.task.NoteAccess) {
            state.task.NoteAccess = state.task.NoteAccess.map((access) => {
              if (access.id == action.payload.deletedAccess)
                return action.payload.accessOption;
              else return access;
            });
          }
        }
      })
      .addCase(replaceTaskAccess.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(replaceTaskAccess.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(deleteTaskAccess.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;

        const noteId = action.payload.noteId;

        if (state.tasks) {
          state.tasks = state.tasks.map((task) => {
            if (task.id === noteId) {
              return {
                ...task,
                NoteAccess: task.NoteAccess.filter(
                  (access) => access.id !== action.payload.accessId,
                ),
              };
            } else return task;
          });
        }

        if (state.task?.NoteAccess) {
          state.task.NoteAccess = state.task.NoteAccess.filter(
            (access) => access.id !== action.payload.accessId,
          );
        }
      })
      .addCase(deleteTaskAccess.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(deleteTaskAccess.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })

      /*
      .addCase(getScheduleItems.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(getScheduleItems.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;

        if (state.task?.schedule) {
          state.task.schedule = [...state.task.schedule, ...action.payload];
        } else if (state.task) {
          state.task.schedule = action.payload;
        }
      })
      .addCase(getScheduleItems.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })

      */
      .addCase(updateScheduleItem.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(updateScheduleItem.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        if (state.task?.schedule) {
          state.task.schedule = state.task?.schedule?.map((scheduleItem) =>
            scheduleItem.id !== action.payload.id
              ? scheduleItem
              : action.payload,
          );
        }
      })
      .addCase(updateScheduleItem.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(deleteScheduleItem.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(deleteScheduleItem.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        if (state.task?.schedule) {
          state.task.schedule = state.task?.schedule?.filter(
            (scheduleItem) => scheduleItem.id !== action.payload.id,
          );
        }
      })
      .addCase(deleteScheduleItem.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(createDelegationRequest.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(createDelegationRequest.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;

        state.tasks = [
          ...state.tasks.map((ta) => {
            if (ta.id === action.payload.issuerNoteId) {
              return { ...ta, delegatedStatus: "REQUEST" as DelegationStatus };
            }
            return ta;
          }),
        ];

        if (state.task && state.task.id === action.payload.issuerNoteId)
          state.task = {
            ...state.task,
            delegatedStatus: "REQUEST",
            DelegationRequest: action.payload,
          };
      })
      .addCase(createDelegationRequest.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(createRequestAccess.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(createRequestAccess.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;

        if (state.task?.DelegationRequest) {
          if (state.task.DelegationRequest?.access) {
            state.task.DelegationRequest.access = [
              ...state.task.DelegationRequest?.access,
              action.payload,
            ];
          } else {
            state.task.DelegationRequest = {
              ...state.task.DelegationRequest,
              access: [action.payload],
            };
          }
        }
        if (state.requestedTask?.access) {
          state.requestedTask.access = [
            ...(state.requestedTask.access || []),
            action.payload,
          ];
        }
      })
      .addCase(createRequestAccess.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(deleteRequestAccess.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(deleteRequestAccess.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        if (state.task?.DelegationRequest) {
          state.task.DelegationRequest.access = [
            ...state.task.DelegationRequest.access,
          ].filter((access) => access.id !== action.payload);
        }
        if (state.requestedTask) {
          state.requestedTask.access = [...state.requestedTask.access].filter(
            (acc) => acc.id !== action.payload,
          );
        }
      })

      .addCase(acceptRequest.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(acceptRequest.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(acceptRequest.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.tasks = [...state.tasks, action.payload];
      })

      .addCase(rejectRequest.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(rejectRequest.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(rejectRequest.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.requestAccesses = [...(state.requestAccesses || [])].filter(
          (acc) => acc.id !== action.payload,
        );
      })
      .addCase(deleteRequestAccess.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(deleteRequest.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(deleteRequest.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        if (state.task && state.task.DelegationRequest) {
          state.task.DelegationRequest = undefined;
          state.task.delegatedStatus = "OPEN";
        }
        state.tasks = [
          ...state.tasks.map((ta) => {
            if (ta.id === action.payload) {
              return { ...ta, delegatedStatus: "OPEN" as DelegationStatus };
            }
            return ta;
          }),
        ];
      })
      .addCase(deleteRequest.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(getRequests.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(getRequests.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.requestAccesses = action.payload;
      })
      .addCase(getRequests.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(getRequestedTask.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
      })
      .addCase(getRequestedTask.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.requestedTask = action.payload;
      })
      .addCase(getRequestedTask.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      });
  },
});

export const {
  resetTaskStatus,
  resetTask,
  setTotalRecordedWorkload,
  setEstimatedWorkload,
  setScheduledTime,
  setRecordingCompletionValue,
  setRecordingStartTime,
  setPomodoroStartTime,
  resetTaskIsCompleted,
  resetArchivedTask,
  setActiveTaskData,
  resetTaskIsNew,
  setTaskIsDeleted,
  setTaskIsCompleted,
} = taskSlice.actions;
export default taskSlice.reducer;
